import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ShieldAlert, Sparkles, Settings, CalendarRange } from 'lucide-react';
import api from '../../../services/api';

export default function ReservationSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState({
    max_tables_limit: 10,
    max_capacity_limit: 40,
    slot_duration_mins: 90,
    min_group_size: 1,
    max_group_size: 10,
    lead_time_hours: 1,
    operating_hours: {
      start: "11:00",
      end: "22:00"
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/reservations/settings');
      setEnabled(response.data.reservations_enabled || false);
      if (response.data.reservation_settings) {
        setSettings({
          ...settings,
          ...response.data.reservation_settings
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: Number(value)
    }));
  };

  const handleOperatingHoursChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [field]: value
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put('/api/reservations/settings', {
        reservations_enabled: enabled,
        reservation_settings: settings
      });
      setSaving(false);
      navigate('/admin/reservations');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update reservation settings');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-500">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <span className="text-sm font-medium">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6 font-sans max-w-4xl">
      
      {/* Header and Back button */}
      <div className="flex items-center gap-3">
        <button 
          onClick={() => navigate('/admin/reservations')}
          className="p-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-all shadow-sm active:scale-95"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarRange className="text-primary-600" />
            Booking Configurations
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure limits, times, and rules for customer table reservations.</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        
        {/* Toggle Status Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="font-bold text-gray-900">Enable Table Booking</h3>
            <p className="text-sm text-gray-500">Allow customers to book tables online via your storefront website.</p>
          </div>
          <button 
            type="button"
            onClick={() => setEnabled(!enabled)}
            className={`w-14 h-8 flex items-center rounded-full p-1 transition-all duration-300 ${
              enabled ? 'bg-primary-600 justify-end' : 'bg-gray-200 justify-start'
            }`}
          >
            <div className="w-6 h-6 rounded-full bg-white shadow-md transform transition-all duration-300"></div>
          </button>
        </div>

        {/* Configurations Fields */}
        <div className={`space-y-6 transition-all duration-300 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          
          {/* Operating hours & Slot Configuration */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 border-b border-gray-50 pb-3 flex items-center gap-2">
              <Settings size={18} className="text-primary-600" />
              Hours & Slots Configuration
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Booking Start Time</label>
                <input 
                  type="time"
                  value={settings.operating_hours?.start || '11:00'}
                  onChange={(e) => handleOperatingHoursChange('start', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Booking End Time</label>
                <input 
                  type="time"
                  value={settings.operating_hours?.end || '22:00'}
                  onChange={(e) => handleOperatingHoursChange('end', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Slot Duration (Mins)</label>
                <select
                  value={settings.slot_duration_mins}
                  onChange={(e) => handleInputChange('slot_duration_mins', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="30">30 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </div>
            </div>
          </div>

          {/* Group sizes & Capacity limits */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 border-b border-gray-50 pb-3 flex items-center gap-2">
              <Sparkles size={18} className="text-primary-600" />
              Seating & Capacity limits
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Maximum Tables Available (per slot)</label>
                <input 
                  type="number"
                  min="1"
                  value={settings.max_tables_limit}
                  onChange={(e) => handleInputChange('max_tables_limit', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Maximum Seating Capacity (per slot)</label>
                <input 
                  type="number"
                  min="1"
                  value={settings.max_capacity_limit}
                  onChange={(e) => handleInputChange('max_capacity_limit', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Minimum Group Size</label>
                <input 
                  type="number"
                  min="1"
                  value={settings.min_group_size}
                  onChange={(e) => handleInputChange('min_group_size', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Maximum Group Size</label>
                <input 
                  type="number"
                  min="1"
                  value={settings.max_group_size}
                  onChange={(e) => handleInputChange('max_group_size', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="pt-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Lead Time Hours (Min. hours in advance required to book)</label>
              <input 
                type="number"
                min="0"
                value={settings.lead_time_hours}
                onChange={(e) => handleInputChange('lead_time_hours', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:w-1/2"
              />
            </div>
          </div>

        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4">
          <button 
            type="button"
            onClick={() => navigate('/admin/reservations')}
            className="px-5 py-2.5 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-semibold transition-all active:scale-95"
          >
            Cancel
          </button>
          
          <button 
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl text-sm font-semibold transition-all active:scale-95 flex items-center gap-2 hover:shadow-lg disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>
    </div>
  );
}
